import type { ComponentChildren } from "preact"
import { exactCurrencyFormatter } from "../../../utils"
import { comparable_dollar_totals_are_effectively_equal } from "../utils/forecast_table_pipeline"

interface ComparableTotals {
  forecast_volume_dollars: number
  actual_volume_dollars: number
}

interface CompareModeRevenueFooterCellProps {
  comparableTotals: ComparableTotals
  fullForecastVolumeDollars: number
  /** When false, promo lines are hidden (Promo lift toolbar off). */
  show_promo_lift?: boolean
  promo_lift_dollars?: number
  promo_lift_units?: number
  /** Override default “QuickBooks actual” caption (e.g. top-down prior vs allocated). */
  subsetCaption?: string
  renderComparison: (args: {
    actualVolumeDollars: number
    forecastVolumeDollars: number
    varianceVolumeDollars: number
    varianceVolumePct: number
  }) => ComponentChildren
}

/** Detailed compare footer appears only when QB comparable actual and forecast differ. */
export function CompareModeRevenueFooterCell({
  comparableTotals,
  fullForecastVolumeDollars,
  show_promo_lift = false,
  promo_lift_dollars = 0,
  promo_lift_units = 0,
  subsetCaption = "Compared rows only (forecast and QuickBooks actual)",
  renderComparison,
}: CompareModeRevenueFooterCellProps) {
  const act = comparableTotals.actual_volume_dollars
  const fcst = comparableTotals.forecast_volume_dollars
  const show_subset_variance_breakdown = !comparable_dollar_totals_are_effectively_equal(act, fcst)
  const variance_vol = act - fcst
  const variance_pct = fcst !== 0 ? (variance_vol / fcst) * 100 : 0

  const show_promo_block =
    show_promo_lift && (promo_lift_dollars > 0 || promo_lift_units > 0)
  const promo_block = show_promo_block ? (
    <div className="mx-auto mt-1 flex w-max flex-col items-end gap-0.5 text-xs font-semibold tabular-nums text-amber-900">
      <span>{exactCurrencyFormatter(promo_lift_dollars)}</span>
      <span className="leading-tight">+{Math.round(promo_lift_units).toLocaleString()}</span>
      <span className="text-[10px] font-medium text-amber-900/90">units</span>
    </div>
  ) : null

  if (!show_subset_variance_breakdown) {
    return (
      <div>
        <div className="text-sm font-medium tabular-nums text-gray-900">
          {exactCurrencyFormatter(fullForecastVolumeDollars)}
        </div>
        {promo_block}
      </div>
    )
  }

  return (
    <div className="space-y-2 text-center">
      <div className="space-y-1">
        <p className="text-[10px] leading-snug text-gray-500">{subsetCaption}</p>
        {renderComparison({
          actualVolumeDollars: act,
          forecastVolumeDollars: fcst,
          varianceVolumeDollars: variance_vol,
          varianceVolumePct: variance_pct,
        })}
      </div>
      <div className="border-t border-gray-200 pt-2">
        <p className="text-[10px] leading-snug text-gray-500">Full forecast (matches Forecast tab)</p>
        <div className="text-sm font-semibold tabular-nums text-gray-900">
          {exactCurrencyFormatter(fullForecastVolumeDollars)}
        </div>
        {promo_block}
      </div>
    </div>
  )
}
